import {useMemo, useState} from 'react';
import {format} from 'date-fns';
import {History, Download, Clock, User} from 'lucide-react';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {DataTable, useDataTable, type Column} from '@common-web/components/data-table';
import {trpc, RouterOutput} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';
import {AUDIT_ACTION_CATEGORIES} from '@common/auditLog/actionCategories';
import {AuditActionBadge, AUDIT_ENTITY_TYPES, entityTypeLabel} from './auditFormat';
import {AuditEntrySheet} from './AuditEntrySheet';

type AuditEntry = RouterOutput['internal']['auditLog']['list']['entries'][number];

type ActivityFilters = {
  entityType: '' | (typeof AUDIT_ENTITY_TYPES)[number];
  actionCategory: '' | (typeof AUDIT_ACTION_CATEGORIES)[number];
  clientId: string;
  actorUserId: string;
  dateRange: '' | '24h' | '7d' | '30d';
};

function dateRangeToFrom(range: ActivityFilters['dateRange']): Date | undefined {
  if (!range) return undefined;
  const d = new Date();
  if (range === '24h') d.setDate(d.getDate() - 1);
  else if (range === '7d') d.setDate(d.getDate() - 7);
  else if (range === '30d') d.setDate(d.getDate() - 30);
  return d;
}

export function ActivityLog() {
  const {toast} = useToast();
  const [selectedEntry, setSelectedEntry] = useState<AuditEntry | null>(null);
  const [exporting, setExporting] = useState(false);

  const table = useDataTable<ActivityFilters>({
    defaultPageSize: 25,
    defaultFilters: {
      entityType: '',
      actionCategory: '',
      clientId: '',
      actorUserId: '',
      dateRange: '',
    },
    syncToUrl: true,
  });

  const {data: filterOptions} = trpc.internal.auditLog.getFilterOptions.useQuery();

  const queryFilters = useMemo(
    () => ({
      entityType: table.filterValues.entityType || undefined,
      actionCategory: table.filterValues.actionCategory || undefined,
      clientId: table.filterValues.clientId || undefined,
      actorUserId: table.filterValues.actorUserId || undefined,
      from: dateRangeToFrom(table.filterValues.dateRange),
      search: table.debouncedSearch || undefined,
    }),
    [
      table.filterValues.entityType,
      table.filterValues.actionCategory,
      table.filterValues.clientId,
      table.filterValues.actorUserId,
      table.filterValues.dateRange,
      table.debouncedSearch,
    ],
  );

  const {data, isLoading, isFetching, error} = trpc.internal.auditLog.list.useQuery({
    ...queryFilters,
    page: table.page,
    pageSize: table.pageSize,
  });

  const utils = trpc.useUtils();

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await utils.internal.auditLog.export.fetch(queryFilters);
      if (!result.csv) {
        toast({title: 'Nothing to export', description: 'No entries match the current filters.'});
        return;
      }
      const blob = new Blob([result.csv], {type: 'text/csv'});
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: 'Export failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  const columns: Column<AuditEntry>[] = [
    {
      id: 'timestamp',
      header: 'When',
      render: (e) => (
        <div className='flex items-center gap-2'>
          <Clock className='h-4 w-4 text-brand-gray' />
          <span className='text-sm text-brand-navy'>
            {format(new Date(e.timestamp), 'MMM d, yyyy h:mm a')}
          </span>
        </div>
      ),
    },
    {
      id: 'entity',
      header: 'Entity',
      render: (e) => (
        <div className='flex flex-col'>
          <span className='text-sm font-medium text-brand-navy'>
            {entityTypeLabel(e.entityType)}
          </span>
          <span className='text-xs text-brand-gray'>{e.entityName || e.entityId}</span>
        </div>
      ),
    },
    {id: 'action', header: 'Action', render: (e) => <AuditActionBadge action={e.action} />},
    {
      id: 'change',
      header: 'Change',
      render: (e) =>
        e.fieldChanged ? (
          <span className='text-sm text-brand-navy'>
            <span className='text-brand-gray'>{e.fieldChanged}: </span>
            <span className='line-through text-red-500'>{e.oldValue ?? '—'}</span>
            {' → '}
            <span className='text-emerald'>{e.newValue ?? '—'}</span>
          </span>
        ) : (
          <span className='text-sm text-brand-gray'>{e.noteText || '—'}</span>
        ),
    },
    {
      id: 'tenant',
      header: 'Tenant',
      render: (e) => <span className='text-sm text-brand-gray'>{e.clientName || '—'}</span>,
    },
    {
      id: 'actor',
      header: 'Actor',
      render: (e) => (
        <div className='flex items-center gap-2'>
          <User className='h-4 w-4 text-brand-gray' />
          <span className='text-sm text-brand-navy'>{e.changedBy}</span>
        </div>
      ),
    },
  ];

  return (
    <div className='min-h-screen bg-background'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='Activity Log'
          description='Cross-tenant change history — who changed what, when'
          icon={<History className='h-6 w-6' />}
          isFetching={isFetching}
          isLoading={isLoading}
        >
          <PageHeaderAction tone='secondary' onClick={handleExport} disabled={exporting}>
            <Download className='h-4 w-4 mr-2' />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </PageHeaderAction>
        </PageHeader>

        <div className='px-8 py-6 max-w-[1440px] mx-auto w-full'>
          <DataTable<AuditEntry, ActivityFilters>
            table={table}
            columns={columns}
            data={data?.entries}
            total={data?.totalCount}
            totalPages={data?.totalPages}
            loading={isLoading}
            error={error ? new Error(error.message) : null}
            emptyMessage='No activity matches the current filters'
            title='Activity'
            entity={{singular: 'entry', plural: 'entries'}}
            searchPlaceholder='Search entity, field, or actor…'
            rowKey={(e) => e.id}
            onRowClick={(e) => setSelectedEntry(e)}
            filters={[
              {
                key: 'entityType',
                label: 'Entity Types',
                type: 'select',
                placeholder: 'All Entities',
                options: AUDIT_ENTITY_TYPES.map((t) => ({value: t, label: entityTypeLabel(t)})),
              },
              {
                key: 'actionCategory',
                label: 'Categories',
                type: 'select',
                placeholder: 'All Categories',
                options: AUDIT_ACTION_CATEGORIES.map((c) => ({value: c, label: c})),
              },
              {
                key: 'clientId',
                label: 'Tenants',
                type: 'select',
                placeholder: 'All Tenants',
                options: (filterOptions?.clients ?? []).map((c) => ({value: c.id, label: c.name})),
              },
              {
                key: 'actorUserId',
                label: 'Actors',
                type: 'select',
                placeholder: 'All Actors',
                options: (filterOptions?.actors ?? []).map((a) => ({value: a.id, label: a.name})),
              },
              {
                key: 'dateRange',
                label: 'Date',
                type: 'select',
                placeholder: 'All Time',
                options: [
                  {value: '24h', label: 'Last 24 hours'},
                  {value: '7d', label: 'Last 7 days'},
                  {value: '30d', label: 'Last 30 days'},
                ],
              },
            ]}
          />
        </div>
      </div>

      {selectedEntry && (
        <AuditEntrySheet entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
      )}
    </div>
  );
}
